// Export de données (Excel, CSV, PDF).
// Les lignes passées proviennent toujours des données réellement affichées
// (recherches, filtres et périodes déjà appliqués côté page).
// Les bibliothèques lourdes (xlsx, jspdf) sont chargées à la demande pour ne
// jamais ralentir le chargement des pages.

import { drawPdfFooter, drawPdfHeader, resolvePdfBrand, type PdfBrand } from "./pdfBrand";
import { loadPdfLibs } from "./pdfLibs";

export type ExportFormat = "xlsx" | "csv" | "pdf";

export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number;
}

export interface ExportOptions {
  /** Nom du fichier sans extension. */
  filename: string;
  /** Titre affiché dans le PDF (par défaut : nom du fichier). */
  title?: string;
  /** Sous-titre optionnel affiché dans le PDF. */
  subtitle?: string;
  /** Marque (nom + logo + coordonnées) affichée dans l'en-tête et le pied de page du PDF. */
  brand?: PdfBrand;
}

const CSV_DELIMITER = ";";

export const EXPORT_FORMAT_META: Record<ExportFormat, { label: string; icon: string; extension: string }> = {
  xlsx: { label: "Excel (.xlsx)", icon: "ri-file-excel-2-line", extension: ".xlsx" },
  csv: { label: "CSV (.csv)", icon: "ri-file-text-line", extension: ".csv" },
  pdf: { label: "PDF (.pdf)", icon: "ri-file-pdf-2-line", extension: ".pdf" },
};

function normalizeCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function escapeCsv(value: string): string {
  const needsQuotes =
    value.includes(CSV_DELIMITER) ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r");
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function buildMatrix<T>(rows: T[], columns: ExportColumn<T>[]): string[][] {
  const header = columns.map((column) => column.header);
  const body = rows.map((row) => columns.map((column) => normalizeCell(column.value(row))));
  return [header, ...body];
}

/** Nettoie le nom de fichier (retire accents, espaces et caractères interdits). */
export function safeFilename(name: string): string {
  return (
    name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9-_]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase() || "export"
  );
}

function stamp(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function exportToCsv<T>(rows: T[], columns: ExportColumn<T>[], options: ExportOptions): void {
  const matrix = buildMatrix(rows, columns);
  const content = matrix.map((line) => line.map(escapeCsv).join(CSV_DELIMITER)).join("\r\n");
  // BOM UTF-8 : garantit un affichage correct des accents dans Excel.
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `${safeFilename(options.filename)}-${stamp()}.csv`);
}

async function exportToXlsx<T>(rows: T[], columns: ExportColumn<T>[], options: ExportOptions): Promise<void> {
  const XLSX = await import("xlsx");
  const matrix = buildMatrix(rows, columns);
  const worksheet = XLSX.utils.aoa_to_sheet(matrix);
  worksheet["!cols"] = columns.map((column) => ({
    wch: Math.min(45, Math.max(12, column.header.length + 4)),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Données");
  XLSX.writeFile(workbook, `${safeFilename(options.filename)}-${stamp()}.xlsx`);
}

async function exportToPdf<T>(rows: T[], columns: ExportColumn<T>[], options: ExportOptions): Promise<void> {
  const { JsPDF, autoTable } = await loadPdfLibs();

  const doc = new JsPDF({
    orientation: columns.length > 6 ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
  });

  const margin = 40;
  const brand = resolvePdfBrand(options.brand);
  let startY = drawPdfHeader(doc, brand, margin);

  doc.setFontSize(15);
  doc.setTextColor(30);
  doc.text(options.title || options.filename, margin, startY);
  startY += 16;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Exporté le ${new Date().toLocaleString("fr-FR")}`, margin, startY);
  startY += 14;
  if (options.subtitle) {
    const subtitleLines = doc.splitTextToSize(options.subtitle, doc.internal.pageSize.getWidth() - margin * 2);
    doc.text(subtitleLines, margin, startY);
    startY += subtitleLines.length * 12;
  }
  startY += 8;

  autoTable(doc, {
    startY,
    head: [columns.map((column) => column.header)],
    body: rows.map((row) => columns.map((column) => normalizeCell(column.value(row)))),
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: [38, 38, 38], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [246, 246, 246] },
    margin: { left: margin, right: margin, bottom: margin + 30 },
  });

  drawPdfFooter(doc, brand, margin);

  doc.save(`${safeFilename(options.filename)}-${stamp()}.pdf`);
}

/**
 * Exporte les lignes dans le format demandé.
 * Lève une erreur explicite si aucune donnée n'est disponible.
 */
export async function exportData<T>(
  format: ExportFormat,
  rows: T[],
  columns: ExportColumn<T>[],
  options: ExportOptions
): Promise<void> {
  if (columns.length === 0) {
    throw new Error("Aucune colonne à exporter.");
  }
  if (rows.length === 0) {
    throw new Error("Aucune donnée à exporter pour les filtres actuels.");
  }

  if (format === "csv") {
    exportToCsv(rows, columns, options);
    return;
  }
  if (format === "xlsx") {
    await exportToXlsx(rows, columns, options);
    return;
  }
  await exportToPdf(rows, columns, options);
}